// src/pages/Tickets/TicketStatusTransitionList.jsx
// -----------------------------------------------------------------------------
// Configures Ticket Status Transitions (Ticket_Status_Transition_TBL):
// which role may move a ticket from one status to another on a project.
// Once a project has at least one transition, the API only allows the moves
// listed here (Admin can always move). Transitions out of a closed status
// control who can reopen a ticket and from where (reopen rules).
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { projectService, ticketStatusService, roleService } from '../../api';
import ticketStatusTransitionService from '../../api/ticketStatusTransitionService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function TicketStatusTransitionList() {
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formProjectID, setFormProjectID] = useState('');

  useEffect(() => {
    projectService.getAll().then((res) => setProjects(res.data || [])).catch(() => {});
    ticketStatusService.getAll().then((res) => setStatuses(res.data || [])).catch(() => {});
    roleService.getAll().then((res) => setRoles(res.data || [])).catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketStatusTransitionService, {
    idKey: 'transitionID',
    itemName: 'Status Transition',
    initialSort: { key: 'projectID', direction: 'asc' },
  });

  // Status dropdowns only offer the selected project's statuses
  useEffect(() => {
    if (isFormOpen) setFormProjectID(selectedRecord?.projectID ? String(selectedRecord.projectID) : '');
  }, [isFormOpen, selectedRecord]);

  const statusName = (id) => statuses.find((s) => Number(s.statusID) === Number(id))?.statusName || `#${id}`;
  const roleName = (id) => roles.find((r) => Number(r.roleID) === Number(id))?.roleName || `#${id}`;
  const projectName = (id) => projects.find((p) => Number(p.projectID) === Number(id))?.projectName || `Project #${id}`;

  const projectStatusOptions = statuses
    .filter((s) => formProjectID && Number(s.projectID) === Number(formProjectID))
    .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
    .map((s) => ({ label: `${s.statusName}${s.isClosedStatusFlag ? ' (closed)' : ''}`, value: s.statusID }));

  const fields = useMemo(() => [
    { name: 'transitionID', label: 'ID', type: 'integer', isPrimaryKey: true, readOnly: true },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    { name: 'fromStatusID', label: 'From Status', type: 'select', required: true, options: projectStatusOptions },
    {
      name: 'toStatusID',
      label: 'To Status',
      type: 'select',
      required: true,
      options: projectStatusOptions,
      validate: (value, values) =>
        value && String(value) === String(values.fromStatusID) ? 'To Status must differ from From Status.' : null,
    },
    {
      name: 'allowedRoleID',
      label: 'Allowed Role',
      type: 'select',
      required: true,
      options: roles.map((r) => ({ label: r.roleName, value: r.roleID })),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [projects, roles, statuses, formProjectID]);

  const handleFieldChange = (fieldName, value, nextFormData) => {
    if (fieldName === 'projectID') {
      setFormProjectID(String(value || ''));
      return { ...nextFormData, fromStatusID: '', toStatusID: '' };
    }
    return nextFormData;
  };

  const columns = [
    { key: 'transitionID', label: 'ID', sortable: true, width: '70px' },
    { key: 'projectID', label: 'Project', sortable: true, render: (row) => projectName(row.projectID) },
    { key: 'fromStatusID', label: 'From', sortable: true, render: (row) => statusName(row.fromStatusID) },
    { key: 'toStatusID', label: 'To', sortable: true, render: (row) => statusName(row.toStatusID) },
    { key: 'allowedRoleID', label: 'Allowed Role', sortable: true, render: (row) => roleName(row.allowedRoleID) },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Status Transitions</h1>
          <div className="subtitle">
            Which role may move a ticket between statuses. Once a project has any transition, only the listed
            moves are allowed (Admin can always move). Moves out of a closed status control reopening.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Transition
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="transitionID"
        emptyMessage="No transitions configured - tickets can move between any statuses."
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortConfig={sortConfig}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {isFormOpen && (
        <TableForm
          fields={fields}
          initialValues={selectedRecord || { projectID: '', fromStatusID: '', toStatusID: '', allowedRoleID: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          onFieldChange={handleFieldChange}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Status Transition"
        />
      )}

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Status Transition"
        itemLabel={
          selectedRecord
            ? `${statusName(selectedRecord.fromStatusID)} → ${statusName(selectedRecord.toStatusID)}`
            : 'Status Transition'
        }
        deleting={deleting}
      />
    </div>
  );
}
